import type { CityPhase } from "./cityRenderer";
import { lightCharacter } from "./characterLighting";

export function drawRooftop(ctx: CanvasRenderingContext2D, phase: CityPhase, time: number) {
  const edge=phase === "night" ? "#7492b1" : phase === "dusk" ? "#eea6ad" : "#a8cbd4";
  const dark="#101b30",shade="#657e9c";
  const rect = (x:number,y:number,w:number,h:number,c:string) => {ctx.fillStyle=c;ctx.fillRect(Math.round(x),Math.round(y),w,h);};
  const poly = (pts:number[][],c:string) => {ctx.fillStyle=c;ctx.beginPath();pts.forEach(([x,y],i)=>i?ctx.lineTo(Math.round(x),Math.round(y)):ctx.moveTo(Math.round(x),Math.round(y)));ctx.closePath();ctx.fill();};
  // Roof parapet, railing and warm rim light frame the view from above.
  poly([[0,286],[480,282],[480,300],[0,300]],dark);
  rect(0,268,480,4,"#0a1426");rect(0,267,480,1,edge);
  for(let x=7;x<480;x+=19){rect(x,272,2,25,"#162039");rect(x+2,272,1,24,shade);}
  rect(0,296,480,3,"#0a1426");
}


export type CharacterPose = "default" | "leanback" | "phone";
export type CharacterMode = "normal" | "smoking" | "coffee";
export type CharacterHair = "layered" | "long";
export type CharacterOutfit = "long" | "short";

export interface CharacterRegistration { mouth: [number, number]; singleFrame: true; }
export function drawCharacter(ctx:CanvasRenderingContext2D, sprite:HTMLImageElement | HTMLCanvasElement, phase:CityPhase, time:number, mode:CharacterMode = "normal", pose:CharacterPose = "default", outfit:CharacterOutfit = "long", registration?: CharacterRegistration, overlay?: HTMLImageElement) {
  const ready = 'naturalWidth' in sprite ? sprite.complete && sprite.naturalWidth > 0 : sprite.width > 0;
  if(ready){
    const column=pose==="default"?0:pose==="leanback"?1:2;
    const row=outfit==="short"?1:0;
    const cellWidth=('naturalWidth' in sprite ? sprite.naturalWidth : sprite.width)/3;
    const cellHeight=('naturalHeight' in sprite ? sprite.naturalHeight : sprite.height)/2;
    const lit = overlay?.complete && overlay.naturalWidth > 0 ? lightCharacter(sprite,overlay,column,row,!!registration) : null;
    if(lit)ctx.drawImage(lit,-8,198);
    else if(registration)ctx.drawImage(sprite,0,0,112,112,-8,198,112,112);
    else ctx.drawImage(sprite,column*cellWidth,row*cellHeight,cellWidth,cellHeight,-8,198,112,112);
    // Mouth anchors are registered to each pose and shared between jacket variants.
    const mouth=registration?{x:registration.mouth[0]-8,y:registration.mouth[1]+198}:pose==="default"?{x:69,y:225}:pose==="leanback"?{x:48,y:212}:{x:57,y:226};
    if(mode==="smoking"&&!registration){
      ctx.fillStyle="#e7e7df";
      if(pose==="leanback")ctx.fillRect(mouth.x,mouth.y-4,1,5);
      else ctx.fillRect(mouth.x,mouth.y,5,1);
      ctx.fillStyle="#f29458";
      ctx.fillRect(mouth.x+(pose==="leanback"?0:5),mouth.y-(pose==="leanback"?5:0),1,1);
    }
    // Iced coffee has no steam; only breath and cigarette smoke emit particles.
    const age=time%(mode==="smoking"?4:9);
    const duration=mode==="smoking"?3:2.1;
    if(mode!=="coffee" && age<duration){
      for(let i=0;i<5;i++){
        ctx.globalAlpha=Math.sin(age/duration*Math.PI)*.22*(1-i/7);
        ctx.fillStyle=phase==="dusk"?"#e9d8df":"#c6e3e8";
        ctx.fillRect(Math.round(mouth.x+age*3+i+Math.sin(time+i)),Math.round(mouth.y-age*4-i*.7),2+i%3,1+i%2);
      }
      ctx.globalAlpha=1;
    }
  }
  drawRooftopProps(ctx,phase,mode!=="coffee");
}

function drawRooftopProps(ctx:CanvasRenderingContext2D, phase:CityPhase, cupOnDeck:boolean) {
  const rect=(x:number,y:number,w:number,h:number,c:string)=>{ctx.fillStyle=c;ctx.fillRect(x,y,w,h);};
  const poly=(points:number[][],color:string)=>{
    ctx.fillStyle=color;ctx.beginPath();
    points.forEach(([x,y],i)=>i?ctx.lineTo(x,y):ctx.moveTo(x,y));
    ctx.closePath();ctx.fill();
  };
  const rim=phase==="dusk"?"#b99889":"#7c9eaa";
  const wood=phase==="dusk"?"#66515a":"#344c5d";
  // A continuous foreground deck anchors the furniture to the near roof edge.
  poly([[108,288],[254,285],[290,300],[98,300]],"#172637");
  for(let y=291;y<302;y+=5)rect(108,y,163,1,"#23394a");
  poly([[122,295],[160,293],[178,300],[116,300]],"#0b1726");
  poly([[171,287],[254,287],[283,300],[169,300]],"#0a1524");

  // Deep counter: visible top, thick apron and supports extending below the crop.
  poly([[166,277],[239,277],[263,287],[155,287]],wood);
  for(let y=279;y<287;y+=3)rect(169,y,69,1,"#425a68");
  rect(155,287,108,4,"#172b3b");
  rect(155,287,108,1,rim);
  poly([[155,291],[263,291],[263,312],[155,309]],"#1c3041");
  rect(159,292,3,22,"#415765");
  rect(249,292,7,22,"#0c1b2b");
  rect(163,292,83,1,"#2b4252");
  // Clear iced-americano cup with dome lid, ice, sleeve and blue straw.
  if(cupOnDeck){
    rect(185,278,21,3,"#213441");
    poly([[185,261],[201,261],[198,279],[188,279]],"#b6c9cb");
    poly([[187,264],[199,264],[197,278],[189,278]],"#51382d");
    rect(188,264,4,3,"#9b8c77");rect(194,265,3,2,"#c0b39a");
    rect(187,269,12,6,"#e4a1b5");
    rect(189,271,7,1,"#bb788f");
    rect(186,261,1,10,"#e6f0e8");rect(199,262,1,7,"#d1e1e1");
    poly([[185,261],[187,256],[190,254],[196,254],[199,256],[201,261]],"#738e9a");
    rect(188,256,2,3,"#d3e8e9");rect(190,254,6,1,"#bbd3d7");
    rect(185,261,16,2,"#c5d5ce");
    rect(193,248,2,15,"#298ecd");rect(194,248,1,12,"#71c2e4");
  }
  // Broad metal ash bin: its body continues below the viewport into the deck.
  poly([[118,276],[126,272],[145,272],[152,276],[148,283],[121,283]],"#3c5262");
  rect(120,276,30,3,rim);
  rect(124,275,22,2,"#152331");
  rect(128,277,13,2,"#253a49");
  poly([[121,280],[149,280],[146,309],[124,309]],"#344b5a");
  rect(123,283,4,23,"#526b79");
  rect(143,283,4,25,"#1a2e40");
  rect(128,284,13,1,"#69818c");
  rect(129,287,10,5,"#162838");
  rect(128,275,6,1,"#d9d0b8");
  rect(134,275,2,1,"#d89161");
}
